import { TYPES } from "@/types";
import { myContainer } from "@/inversify.config";
import { IEmploymentService } from "@/interfaces/IEmploymentService";
import * as dtos from "@/models/dtos";
import { FormBuilder2 } from "../../models/FormBuilder2";
import {
  MetaData,
  DynamicProperty,
  PositionTemplateRequest,
} from "../../models/dtos";
import FormInputListImport from "@/features/shared/components/form/FormInputList.vue";
import { FormItemModel } from "../../models/CheckboxGroup";
import { markRaw } from "vue";

// markRaw hindrar Vue från att göra komponentdefinitionen reactive
// när den tilldelas till editView på ett FormItemModel som ligger i
// reactive store-state.
const FormInputList = markRaw(FormInputListImport);
import { PositionTemplateFolder } from "@/models/PositionTemplateFolder";
import { Buffer } from "buffer";

import { PositionTemplateAsset } from "@/models/PositionTemplateFolder";
import moment from "moment";
import { ToastNotification } from "@/models/ToastNotification";
const client = myContainer.get<IEmploymentService>(TYPES.IEmploymentService);

export default {
  namespaced: true,
  state: {
    form: {} as FormBuilder2,
    formBuillderPosition: new dtos.PositionResponse(),
    formbuilderCurrentView: {} as FormItemModel,
    counter: 1000,
    formTemplateFolder: {} as PositionTemplateFolder,
    formTemplates: [] as PositionTemplateAsset[],
  },

  getters: {
    form: (state: any) => state.form,
    formView: (state: any) => state.formbuilderCurrentView,
    formTemplateFolder(state: any) {
      return state.formTemplateFolder;
    },
    formTemplateAsset: (state: any) => (formTemplateKey: string) => {
      return state.formTemplateFolder.Assets.find(
        (x: PositionTemplateAsset) => x.data.key === formTemplateKey
      );
    },
    formTemplates: (state: any) => state.formTemplates,
    formTemplate: (state: any) => (id: number | string) => {
      if (typeof id === "string") {
        return state.formTemplates.find(
          (x: PositionTemplateAsset) => x.data.key === id
        );
      }
      return state.formTemplates.find(
        (x: PositionTemplateAsset) => x.id === id
      );
    },
    formTemplatesByPositionId: (state: any) => (positionTypeId: number) => {
      return state.formTemplates.filter((x: PositionTemplateRequest) => {
        return x.availableInPositionTypes.find(
          (y) => y.positionTypeId === positionTypeId
        );
      });
    },
  },

  mutations: {
    setFrombuilder(state: any, item: MetaData) {
      state.form = new FormBuilder2(item, false);
    },
    setFrombuilder2(state: any, item: MetaData) {
      state.form = new FormBuilder2(item, true);
    },
    resetFormbuilder(state: any) {
      state.form = new FormBuilder2(new MetaData(), false);
    },
    updateFormMetadata(state: any, metadata: MetaData) {
      // Update the existing form's metadata without recreating the FormBuilder2 instance
      if (state.form && state.form.metadata) {
        state.form.metadata = metadata;
      }
    },
    updateRadioSelection(state: any, payload: { parentKey: string, selectedKey: string }) {
      // Check if form and metadata exist
      if (!state.form || !state.form.metadata || !state.form.metadata.properties) {
        console.warn('Form metadata not available for radio selection update');
        return;
      }

      // Find all radio buttons in the group
      const radioGroup = state.form.metadata.properties.find(
        (x: DynamicProperty) => x.key === payload.parentKey
      );

      if (!radioGroup || !radioGroup.data || !radioGroup.data.keys) return;
      
      // Update all radio buttons in the group
      radioGroup.data.keys.forEach((key: string) => {
        const radioButton = state.form.metadata.properties.find(
          (x: DynamicProperty) => x.key === key
        );
        if (radioButton) {
          // Set true for selected, false for others
          radioButton.data = (key === payload.selectedKey);
        }
      });
      
      // Also update the Items array if it exists
      if (state.form.Items) {
        state.form.Items.forEach((item: any) => {
          if (radioGroup.data.keys.includes(item.item.key)) {
            item.item.data = (item.item.key === payload.selectedKey);
          }
        });
      }
    },
    setFormView(state: any, item: any) {
      state.formbuilderCurrentView = item;
    },
    setPosition(state: any, item: dtos.PositionResponse) {
      state.formBuillderPosition = item;
    },
    setFormTemplateFolder(state: any, item: PositionTemplateFolder) {
      state.formTemplateFolder = item;
    },
    setFormTemplates(state: any, item: PositionTemplateRequest[]) {
      state.formTemplates = item;
    },
  },

  actions: {
    async setPosition({ commit }: any, item: dtos.PositionResponse) {
      commit("setPosition", item);
    },
    async setFrombuilder({ commit }: any, item: MetaData) {
      commit("setFrombuilder", item);
      const _item = new FormItemModel(FormInputList, new DynamicProperty());
      _item.editView = FormInputList;
      commit("setFormView", _item);
    },
    resetFormbuilder({ commit }: any) {
      commit("resetFormbuilder");
    },
    // Action to trigger reactivity without recreating FormBuilder2
    updateFormReactivity({ state }: any) {
      // Force Vue to detect changes by touching the form object
      if (state.form && state.form.Items) {
        // Trigger reactivity by reassigning the array
        state.form.Items = [...state.form.Items];
      }
    },
    async setFrombuilder2({ commit }: any, item: MetaData) {
      commit("setFrombuilder2", item);
      const _item = new FormItemModel(FormInputList, new DynamicProperty());
      _item.editView = FormInputList;
      commit("setFormView", _item);
    },
    setFormView({ commit }: any, item: any) {
      commit("setFormView", item);
    },
    async loadFormTemplateFolder({ getters, dispatch, commit, rootGetters }: any) {
      let folderId = rootGetters?.getPositionTemplateFolder?.id;
      if (!folderId) await dispatch("loadContainerFolders", null, { root: true });

      folderId = rootGetters?.getPositionTemplateFolder?.id;
      if (!folderId)
        await dispatch("createContainerFolder", "PositionTemplates", { root: true });

      folderId = rootGetters?.getPositionTemplateFolder?.id;
      if (!folderId)
        throw new Error("Hittade inga mallar, vänligen kontakta support");

      const folder = await client.Container.getFolder(
        rootGetters.container.id,
        folderId
      );
      const templateFolder = new PositionTemplateFolder(folder);
      commit("setFormTemplateFolder", templateFolder);
      commit("setFormTemplates", templateFolder.Assets);
    },

    async loadFormTemplates({ getters, commit, dispatch, rootGetters }: any) {
      let folderId = rootGetters?.getPositionTemplateFolder?.id;
      if (!folderId) await dispatch("loadContainerFolders", null, { root: true });

      folderId = rootGetters?.getPositionTemplateFolder?.id;

      const folder = await client.Container.getFolder(
        rootGetters.container.id,
        folderId
      );
      const model = new PositionTemplateFolder(folder);

      commit("setFormTemplates", model.Assets);
    },
    async loadFormTemplate(
      { getters, dispatch, state }: any,
      id: number | string
    ) {
      if (state.formTemplates.length === 0) await dispatch("loadFormTemplates");
      if (state.formTemplates.length === 0) throw new Error("No templates");

      return getters.formTemplate(id).data as PositionTemplateRequest;
    },
    async getOrCreateFormTemplate(
      { getters, dispatch, rootGetters }: any,
      id?: number | string
    ) {
      if (id) {
        return await dispatch("loadFormTemplate", id);
      } else {
        const template = new PositionTemplateRequest();
        template.folderId = rootGetters?.getPositionTemplateFolder?.id;
        template.key = crypto.randomUUID();
        template.name = "";
        template.description = "";
        template.createdAt = moment().unix();
        template.createdBy = rootGetters?.getLoggedInUser as dtos.UserResponse;
        template.availableInPositionTypes =
          rootGetters?.container?.positionTypes.map((x: any) => {
            return {
              positionTypeId: x.id,
              positionTypeName: x.name,
              included: false,
              default: false,
            };
          });
        template.metadata = new MetaData();

        return template;
      }
    },
    async saveFormTemplate(
      { getters, dispatch, state, commit, rootGetters }: any,
      template: PositionTemplateRequest
    ) {
      // check if template already exists
      const existingTemplate = state.formTemplates.find(
        (x: PositionTemplateAsset) => x.data.key === template.key
      );

      let id = 0;
      if (existingTemplate) id = existingTemplate.id;

      // encode template to base64
      const encode = (str: string): string =>
        Buffer.from(str, "utf-8").toString("base64");

      // convert template to json
      const dataString = JSON.stringify(template);

      // create new template
      await client.Container.createPositionTemplateAsset(
        id,
        rootGetters.container.id,
        rootGetters.getPositionTemplateFolder.id,
        encode(dataString),
        "application/json",
        template.name
      );
      await dispatch("reloadContainer", null, { root: true });
      await dispatch("loadFormTemplates"); // reload templates from backend
    },
    async deleteFormTemplate({ getters, dispatch, rootGetters }: any, templateId: number) {
      await client.Container.deleteContainerAsset(
        rootGetters.container.id,
        rootGetters.getPositionTemplateFolder.id,
        templateId
      );
      await dispatch(
        "addNotification",
        new ToastNotification("success", "Mall borttagen"),
        { root: true }
      );
      await dispatch("reloadContainer", null, { root: true });
      await dispatch("loadFormTemplates"); // reload templates from backend
    },
    selectRadioButton(
      { getters, commit, rootGetters, state }: any,
      item: {
        parentKey: string;
        keyToSelect: string;
        adminView?: boolean;
      }
    ) {
      // If form metadata is not available, try to get it from the applicant
      if (!state.form || !state.form.metadata || !state.form.metadata.properties) {
        const applicantMetadata = rootGetters.positionApplicant?.Applicant?.metadata;
        if (applicantMetadata) {
          commit('setFrombuilder', applicantMetadata);
        }
      }

      // Use the mutation to update the radio selection
      commit('updateRadioSelection', {
        parentKey: item.parentKey,
        selectedKey: item.keyToSelect
      });
    },
    toggleIsRequired(
      { getters, commit }: any,
      item: {
        key: string;
        value: boolean;
      }
    ) {
      const form = getters.form;
      if (!form?.metadata?.properties) return;

      const propToChange = form.metadata.properties.find(
        (x: DynamicProperty) => x.key === item.key
      );
      if (!propToChange) return;

      // Initialize validators array if it doesn't exist
      if (!propToChange.validators) {
        propToChange.validators = [];
      }

      const validator = propToChange?.validators?.find(
        (validator: any) => validator.name === "radioRequired"
      );

      if (validator) {
        propToChange?.validators.splice(
          propToChange?.validators.indexOf(validator),
          1
        );
      }

      if (item.value) {
        const required = new dtos.PropertyValidator("radioRequired", item.value);
        propToChange?.validators.push(required);
      }

      // Use the new mutation to update metadata without recreating FormBuilder2
      commit("updateFormMetadata", form.metadata);
    },
  },
};
